import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import {
  IndicadoresService,
  TiendaCatalogoApi,
} from '../../indicadores/services/indicadores.service';
import { VendedoresService } from '../services/vendedores.service';
import { MovimientoTemporal, SelectorItem } from '../types/vendedores.types';

/**
 * Vendedores por tienda: ver lo que ve el vendedor, y mover gente por temporada.
 *
 * POR QUÉ EXISTE
 * Por temporada se mueven colaboradores entre tiendas y baja personal de
 * corporativo a piso. El selector se arma de BUK, que tarda semanas en
 * reflejarlo: el colaborador NO aparece donde trabaja y SIGUE apareciendo donde
 * salió, con el riesgo de que lo seleccionen por error y generen solicitudes a
 * su nombre en la tienda equivocada.
 *
 * Cada alta o baja refresca EN EL ACTO el selector de las dos tiendas
 * implicadas —y de ninguna otra—, así que el cambio se ve sin esperar las 24 h
 * que dura la caché.
 */
const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const VendedoresScreen: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [tiendas, setTiendas] = useState<TiendaCatalogoApi[]>([]);
  const [tienda, setTienda] = useState<TiendaCatalogoApi | null>(null);
  const [selector, setSelector] = useState<SelectorItem[] | null>(null);
  const [cargandoSelector, setCargandoSelector] = useState(false);
  const [movimientos, setMovimientos] = useState<MovimientoTemporal[]>([]);
  const [descartados, setDescartados] = useState<string[]>([]);
  const [guardando, setGuardando] = useState(false);

  // Alta
  const [empleado, setEmpleado] = useState<number | null>(null);
  const [nombre, setNombre] = useState('');
  const [origen, setOrigen] = useState<TiendaCatalogoApi | null>(null);
  const [hasta, setHasta] = useState<Date | null>(null);
  const [motivo, setMotivo] = useState('');

  const avisar = (severity: 'success' | 'warn' | 'error', summary: string, detail?: string) =>
    toast.current?.show({ severity, summary, detail, life: severity === 'success' ? 4000 : 9000 });

  const cargarMovimientos = useCallback(async () => {
    try {
      const r = await VendedoresService.listarMovimientos();
      setMovimientos(r.movimientos ?? []);
      setDescartados(r.descartados ?? []);
    } catch (e) {
      avisar('error', 'No se pudieron leer los movimientos', (e as Error).message);
    }
  }, []);

  useEffect(() => {
    IndicadoresService.getCatalogoTiendas()
      .then((c) => setTiendas(c.tiendas ?? []))
      .catch((e) => avisar('error', 'No se pudo leer el catálogo de tiendas', (e as Error).message));
    void cargarMovimientos();
  }, [cargarMovimientos]);

  const verSelector = useCallback(async (t: TiendaCatalogoApi) => {
    setCargandoSelector(true);
    setSelector(null);
    try {
      setSelector(await VendedoresService.getSelector(t.chain, t.num));
    } catch (e) {
      avisar('error', 'No se pudo leer el selector', (e as Error).message);
    } finally {
      setCargandoSelector(false);
    }
  }, []);

  useEffect(() => {
    if (tienda) void verSelector(tienda);
  }, [tienda, verSelector]);

  const opcionesTienda = useMemo(
    () =>
      [...tiendas]
        .sort((a, b) => a.chain.localeCompare(b.chain) || a.num - b.num)
        .map((t) => ({ label: `${t.chain} ${t.num} · ${t.tienda}`, value: t })),
    [tiendas],
  );

  const puedeGuardar =
    !!tienda && !!empleado && empleado > 0 && !!hasta && (!origen || origen.num !== tienda.num);

  const guardar = async () => {
    if (!tienda || !empleado || !hasta) return;
    setGuardando(true);
    try {
      const r = await VendedoresService.guardarMovimiento(tienda.chain, {
        empleado,
        nombre: nombre.trim() || undefined,
        origen: origen ? origen.num : null,
        destino: tienda.num,
        hasta: iso(hasta),
        motivo: motivo.trim() || undefined,
      });
      if (r.aplicado) {
        avisar('success', 'Asignado', r.detalle);
      } else {
        // Guardado pero SIN efecto. Se dice ahora, no cuando la tienda lo reporte.
        avisar('warn', 'Guardado, pero NO aparece en el destino', r.detalle);
      }
      setEmpleado(null);
      setNombre('');
      setOrigen(null);
      setHasta(null);
      setMotivo('');
      await cargarMovimientos();
      await verSelector(tienda);
    } catch (e) {
      avisar('error', 'No se pudo guardar', (e as Error).message);
    } finally {
      setGuardando(false);
    }
  };

  const quitar = (m: MovimientoTemporal) => {
    if (!tiendas.length) return;
    const cadena = tiendas.find((t) => t.num === m.destino)?.chain ?? tienda?.chain;
    if (!cadena) {
      avisar('error', 'No sé de qué cadena es la tienda destino');
      return;
    }
    confirmDialog({
      message: `${m.nombre ?? `Colaborador ${m.empleado}`} vuelve a su tienda de BUK. Se refrescan las dos tiendas implicadas.`,
      header: 'Regresar a su tienda',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sí, regresar',
      rejectLabel: 'Cancelar',
      accept: async () => {
        try {
          await VendedoresService.quitarMovimiento(cadena, m.empleado);
          avisar('success', 'Movimiento retirado');
          await cargarMovimientos();
          if (tienda) await verSelector(tienda);
        } catch (e) {
          avisar('error', 'No se pudo retirar', (e as Error).message);
        }
      },
    });
  };

  const nombreTienda = (num?: number | null) => {
    if (num === null || num === undefined) return 'Corporativo';
    const t = tiendas.find((x) => x.num === num);
    return t ? `${t.chain} ${t.num} · ${t.tienda}` : String(num);
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <h2 className="text-2xl font-semibold text-gray-800 mb-2">Vendedores por tienda</h2>
      <p className="text-gray-500 mb-6" style={{ maxWidth: 760 }}>
        Muestra <strong>exactamente lo que ve el vendedor</strong> al entrar, y permite mover a
        alguien a otra tienda por temporada. Al asignar o retirar, el selector de las{' '}
        <strong>dos tiendas implicadas</strong> se refresca en el acto: no hay que esperar.
      </p>

      <div className="mb-3" style={{ maxWidth: 460 }}>
        <label htmlFor="tienda" style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
          Tienda
        </label>
        <Dropdown
          inputId="tienda"
          value={tienda}
          options={opcionesTienda}
          onChange={(e) => setTienda(e.value)}
          filter
          placeholder="Elige una tienda"
          className="w-full"
        />
      </div>

      {descartados.length > 0 && (
        <Message
          severity="error"
          className="mb-3 w-full"
          text={`Hay ${descartados.length} movimiento(s) mal formados que se están ignorando: ${descartados.join(' · ')}`}
        />
      )}

      {tienda && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Quién aparece en {tienda.chain} {tienda.num}{' '}
              {selector && <Tag value={`${selector.length}`} severity="info" />}
            </h3>
            <DataTable
              value={selector ?? []}
              loading={cargandoSelector}
              emptyMessage="Sin personas en el selector"
              size="small"
              scrollable
              scrollHeight="420px"
              dataKey="externalUserId"
            >
              <Column field="userCode" header="Código" style={{ width: 90 }} />
              <Column field="name" header="Nombre" />
              <Column field="employeeNumber" header="N° colab." style={{ width: 110 }} />
              <Column
                header="Origen"
                style={{ width: 150 }}
                body={(r: SelectorItem) => {
                  const mov = movimientos.find(
                    (m) => m.empleado === r.employeeNumber && m.vigente && m.destino === tienda.num,
                  );
                  if (mov) return <Tag value="Temporal" severity="warning" />;
                  if (r.pendingProscai) return <Tag value="Sin código Proscai" severity="info" />;
                  return <span style={{ color: '#9ca3af' }}>Plantilla</span>;
                }}
              />
            </DataTable>
          </div>

          <div className="lg:col-span-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Asignar a esta tienda</h3>
            <div className="flex flex-col gap-4" style={{ maxWidth: 420 }}>
              <div className="flex flex-col gap-1">
                <label htmlFor="empleado" style={{ fontWeight: 600 }}>
                  N° de colaborador (BUK)
                </label>
                <InputNumber
                  inputId="empleado"
                  value={empleado}
                  onValueChange={(e) => setEmpleado(e.value ?? null)}
                  useGrouping={false}
                  placeholder="12345"
                  className="w-full"
                  inputClassName="w-full"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="nombre" style={{ fontWeight: 600 }}>
                  Nombre <span style={{ fontWeight: 400, color: '#6b7280' }}>(opcional)</span>
                </label>
                <InputText
                  id="nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Para reconocerlo en la lista"
                  className="w-full"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="origen" style={{ fontWeight: 600 }}>
                  Tienda de origen{' '}
                  <span style={{ fontWeight: 400, color: '#6b7280' }}>
                    (vacío = viene de corporativo)
                  </span>
                </label>
                <Dropdown
                  inputId="origen"
                  value={origen}
                  options={opcionesTienda}
                  onChange={(e) => setOrigen(e.value)}
                  filter
                  showClear
                  placeholder="Sin tienda de origen"
                  className="w-full"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="hasta" style={{ fontWeight: 600 }}>
                  Hasta
                </label>
                <Calendar
                  inputId="hasta"
                  value={hasta}
                  onChange={(e) => setHasta((e.value as Date) ?? null)}
                  dateFormat="dd/mm/yy"
                  minDate={new Date()}
                  showIcon
                  className="w-full"
                />
                <small style={{ color: '#6b7280' }}>
                  Obligatorio: pasada esa fecha vuelve solo a su tienda, sin que nadie haga nada.
                </small>
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="motivo" style={{ fontWeight: 600 }}>
                  Motivo <span style={{ fontWeight: 400, color: '#6b7280' }}>(opcional)</span>
                </label>
                <InputText
                  id="motivo"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="apoyo de temporada"
                  className="w-full"
                />
              </div>

              <div className="flex flex-col gap-1">
                <Button
                  label="Asignar temporalmente"
                  icon="pi pi-user-plus"
                  onClick={guardar}
                  disabled={!puedeGuardar || guardando}
                  loading={guardando}
                />
                <small style={{ color: '#6b7280' }}>
                  Dejará de aparecer en su tienda de origen y aparecerá aquí. No mueve la venta: el
                  tablero atribuye por el ticket del ERP, no por esta asignación.
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      <h3 className="text-lg font-semibold text-gray-800 mt-8 mb-2">Movimientos</h3>
      <DataTable
        value={movimientos}
        emptyMessage="Sin movimientos temporales"
        size="small"
        dataKey="empleado"
      >
        <Column
          header="Colaborador"
          body={(m: MovimientoTemporal) => m.nombre ?? `#${m.empleado}`}
        />
        <Column header="N°" field="empleado" style={{ width: 90 }} />
        <Column header="De" body={(m: MovimientoTemporal) => nombreTienda(m.origen)} />
        <Column header="A" body={(m: MovimientoTemporal) => nombreTienda(m.destino)} />
        <Column header="Hasta" field="hasta" style={{ width: 120 }} />
        <Column header="Motivo" field="motivo" />
        <Column
          header="Estado"
          style={{ width: 110 }}
          body={(m: MovimientoTemporal) =>
            m.vigente ? (
              <Tag value="Vigente" severity="success" />
            ) : (
              <Tag value="Caducado" severity="secondary" />
            )
          }
        />
        <Column
          style={{ width: 130 }}
          body={(m: MovimientoTemporal) => (
            <Button
              label="Regresar"
              icon="pi pi-undo"
              size="small"
              outlined
              onClick={() => quitar(m)}
            />
          )}
        />
      </DataTable>
    </div>
  );
};

export default VendedoresScreen;
